// Typed wrappers around /api/v1/portfolio-stress-test/* — the single
// point of contact between the SPA and the FastAPI stress-test surface.
//
// Mirrors the pattern in features/risk-assessment/api/riskApi.ts:
//   * reuses the shared apiClient so JWT attach + 401 handling stay in
//     one place,
//   * exports a small ``stressTestApi`` namespace object so feature
//     components import a single symbol,
//   * keeps the wire types right next to the wrappers so a backend
//     contract drift is loud on the next ``tsc`` run.

import { apiClient } from '../../../api/client';

/** Map of scenario name -> { asset class -> percent return }. Returned
 *  verbatim by GET /scenarios so the SPA can render the dropdown +
 *  drill-down without burning the catalogue into the bundle. */
export type ScenarioCatalogue = Record<string, Record<string, number>>;

export interface ScenariosResponse {
    scenarios: ScenarioCatalogue;
}

/** Single row of the per-asset breakdown returned by /stress-test. */
export interface PerAssetImpact {
    asset: string;
    weight_pct: number;
    asset_return_pct: number;
    before_value: number;
    after_value: number;
    absolute_change: number;
}

/** One year-end snapshot of the simulated portfolio trajectory,
 *  returned by /stress-test for the time-series line chart. */
export interface ChartDataPoint {
    year: number;
    portfolio_value: number;
    is_shock_year: boolean;

    // --- NEW: Time-series sensitivity metrics ---
    portfolio_value_no_fee?: number;
    cumulative_dividends_net?: number;
    cumulative_dividends_gross?: number;
}

/** Cumulative cash-flow totals over the whole simulation. */
export interface SimulationMetrics {
    total_contributions: number;
    total_fees_paid: number;
    total_dividends_net: number;
    total_taxes_paid: number;

    // --- NEW: Context-aware shock metrics ---
    first_shock_year: number | null;
    first_shock_before_value: number | null;
    first_shock_after_value: number | null;
    net_gain_vs_initial: number;
    net_gain_vs_invested: number;
    break_even_year: number | null;

    // --- NEW: Liquidation metrics ---
    liquidation_cost_basis: number;
    estimated_liquidation_tax: number;
    projected_value_after_tax: number;
}

/** Allowed cadence for contributions / dividends. Mirrors the backend
 *  ``Literal`` so an unsupported value is a compile-time error. */
export type Periodicity = 'monthly' | 'quarterly' | 'semi-annually' | 'annually';

/** Request body for POST /stress-test. */
export interface StressTestRequest {
    /** Recommended allocation, keyed by canonical asset-class name.
     *  Values are percent weights (0..100) that should sum to ~100. */
    allocation: Record<string, number>;
    /** One of the keys returned by GET /scenarios. */
    scenario: string;
    /** Starting investment amount (currency-agnostic). */
    investment_amount: number;
    /** How many years of compounding to apply to the post-shock value
     *  for the recovery-projection bar. ``0`` disables the projection. */
    time_horizon_years: number;

    // --- Advanced cash-flow & timing params (all optional; the backend
    //     supplies safe defaults so older callers keep working). ---
    /** Recurring contribution added to the portfolio each period. */
    contribution_amount?: number;
    /** How often the recurring contribution is applied. */
    contribution_periodicity?: Periodicity;
    /** When true, dividends are paid out (leave the compounding pool). */
    is_dividend_distributing?: boolean;

    /** Annual dividend yield percentage (e.g. 2.0). */
    dividend_yield_pct?: number;            // <-- CHANGED

    /** How often distributing dividends are paid out. */
    dividend_periodicity?: Periodicity;

    /** Tax rate (percent) withheld from distributed dividends. */
    capital_gains_tax_pct?: number;

    /** Annual AUM management fee percentage. */
    fee_pct_annual?: number;                // <-- CHANGED
    /** Years (0-indexed; 0 == today) in which the scenario shock hits. */
    scenario_occurrence_years?: number[];
}

/** Response body for POST /stress-test. */
export interface StressTestResponse {
    scenario: string;
    investment_amount: number;
    time_horizon_years: number;
    /** Percentage portfolio change from the one-shot shock (-100..+∞). */
    portfolio_change_pct: number;
    before_value: number;
    /** Value immediately after the scenario has played out. */
    after_value: number;
    /** Signed monetary delta (after_value - before_value). */
    absolute_change: number;
    /** Post-shock value compounded at the portfolio's weighted long-run
     *  expected return out to ``time_horizon_years``. */
    projected_value: number;
    /** Static long-run weighted expected return of the allocation.
     *  Useful as the underlying drift that compounds the projection,
     *  but DOES NOT change with scenario / horizon -- so it must not
     *  be displayed as "the projection's per-year rate" (see
     *  ``projected_cagr_pct`` for that). */
    weighted_expected_annual_return_pct: number;
    /** Blended CAGR from ``investment_amount`` (today) to
     *  ``projected_value`` (at horizon). This rate DOES move with both
     *  the scenario and the horizon -- short horizons are dominated by
     *  the one-shot shock, long horizons amortise it back toward the
     *  long-run rate. This is the field the SPA shows as "X % / yr". */
    projected_cagr_pct: number;
    per_asset: PerAssetImpact[];
    allocation_total_pct: number;

    /** Per-year portfolio-value trajectory for the time-series chart. */
    chart_data: ChartDataPoint[];
    /** Cumulative cash-flow totals (contributions, fees, dividends, tax). */
    metrics: SimulationMetrics;
}

export const stressTestApi = {
    /** GET /portfolio-stress-test/scenarios — catalogue for the dropdown. */
    async getScenarios(): Promise<ScenariosResponse> {
        const { data } = await apiClient.get<ScenariosResponse>(
            '/portfolio-stress-test/scenarios',
        );
        return data;
    },

    /** POST /portfolio-stress-test/stress-test — compute the impact. */
    async runStressTest(
        payload: StressTestRequest,
    ): Promise<StressTestResponse> {
        const { data } = await apiClient.post<StressTestResponse>(
            '/portfolio-stress-test/stress-test',
            payload,
        );
        return data;
    },
};
